import { useEffect, useRef, useState } from 'react';

import { useAddToast } from '../../../contexts/ToastContext';
import { useResendEmailVerification } from '../../../hooks/api/auth/email/useResendEmailVerification';
import Button from '../../generic/Button';
import VerificationCodeInput, { type VerificationCodeInputHandle } from '../../generic/Forms/VerificationCodeInput';
import { presentRequestError } from '../errorPresentation';
import { useVerifyEmail } from '../hooks/useVerifyEmail';
import type { VerifyEmailSignupResponse } from '../../../../shared/types/routes/auth/emailVerification';

import styles from '../Login.module.less';

const RESEND_COOLDOWN_SECONDS = 60;

export type VerifyEmailStepProps = {
  /** The address the code was sent to, shown in the instructions. */
  email: string;
  /**
   * When set, a "Do this later" link is offered. Only when the user is joining an existing org (invite / SSO /
   * domain-claim) — that org's trust already stands in for verification, so it can be deferred. A new-org creator
   * must verify first.
   */
  onSkip?: () => void;
  /** Receives the confirm response for a pending-signup verification (where to land); undefined otherwise. */
  onVerified: (result?: VerifyEmailSignupResponse) => void;
};

/**
 * Email-verification challenge shown before onboarding for a cloud signup whose email isn't verified yet: the
 * user enters the code sent to their inbox. Mirrors the MFA step's code entry + resend cooldown, and reuses the
 * shared verify/resend endpoints.
 */
const VerifyEmailStep = ({ email, onSkip, onVerified }: VerifyEmailStepProps) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const verifyEmail = useVerifyEmail();
  const addToast = useAddToast();
  const resendVerification = useResendEmailVerification({
    // A skipped resend (the backend sent nothing) must not pass silently — without feedback the click looks dead.
    onSkipped: () => addToast({ type: 'error', content: 'Could not resend the code. Please try again.' }),
  });
  const codeInputRef = useRef<VerificationCodeInputHandle | null>(null);
  // The settle effect below re-runs whenever the parent re-renders (its inline `onVerified` gets a new
  // identity) while the mutation result stays latched, so guard it to fire once per settled submission.
  const hasHandledResultRef = useRef(false);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // React to the settled result from an effect rather than the mutate callback: navigating on success from the
  // callback unmounts this step while React Query is still about to update it (an unmounted-update warning under
  // React 17). Deferring to the settled state lets this step re-render first.
  useEffect(() => {
    if (hasHandledResultRef.current) {
      return;
    }
    if (verifyEmail.isSuccess) {
      hasHandledResultRef.current = true;
      onVerified(verifyEmail.data);
      return;
    }
    if (!verifyEmail.isError) {
      return;
    }
    hasHandledResultRef.current = true;
    const presented = presentRequestError(verifyEmail.error, {
      inlineFallback: 'Invalid code. Please try again.',
      toastFallback: 'Something went wrong. Please try again.',
    });
    if (presented.kind === 'toast') {
      addToast({ type: 'error', content: presented.message });
      return;
    }
    setCode('');
    setError(presented.message);
    codeInputRef.current?.focusFirst();
  }, [verifyEmail.isSuccess, verifyEmail.data, verifyEmail.isError, verifyEmail.error, onVerified, addToast]);

  const handleSubmit = (submittedCode: string = code) => {
    if (!submittedCode || verifyEmail.isPending) {
      return;
    }
    hasHandledResultRef.current = false;
    setError(null);
    verifyEmail.mutate({ code: submittedCode });
  };

  const handleResend = () => {
    if (resendCooldown > 0 || resendVerification.isPending) {
      return;
    }
    resendVerification.mutate(undefined, {
      // Start the cooldown only once the email actually sent, so a failed/not-sent resend keeps the button enabled
      // for an immediate retry instead of stranding the user for 60s (a send failure throws, handled by the hook).
      onSuccess: (response) => {
        if (response.data?.sent) {
          setResendCooldown(RESEND_COOLDOWN_SECONDS);
        }
      },
    });
  };

  return (
    <div className={styles.card}>
      <h1 className={styles.heading}>Verify your email</h1>
      <p className={styles.placeholder}>Enter the 6-digit code we sent to {email}.</p>
      <div className={styles.codeField}>
        <VerificationCodeInput
          ref={codeInputRef}
          value={code}
          onChange={setCode}
          onComplete={handleSubmit}
          disabled={verifyEmail.isPending}
        />
      </div>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <Button isFullWidth size="large" isDisabled={!code || verifyEmail.isPending} onClick={() => handleSubmit()}>
        Verify
      </Button>
      <Button
        type="anchor"
        size="small"
        className={styles.secondaryAction}
        isDisabled={resendCooldown > 0 || resendVerification.isPending}
        onClick={handleResend}
      >
        {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
      </Button>
      {onSkip && (
        <Button
          type="anchor"
          size="small"
          className={styles.secondaryAction}
          isDisabled={verifyEmail.isPending}
          onClick={onSkip}
        >
          Do this later
        </Button>
      )}
    </div>
  );
};

export default VerifyEmailStep;
